import React from 'react';
import { theme } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import type { WorkplaceSovStep } from '../../types/workplace';

interface WorkplaceProgressBarProps {
  currentStep: WorkplaceSovStep;
  onStepClick: (step: WorkplaceSovStep) => void;
}

const STEPS: { key: WorkplaceSovStep; labelKey: string }[] = [
  { key: 'infrastructure', labelKey: 'workplace.steps.infrastructure' },
  { key: 'ist-zustand', labelKey: 'workplace.steps.istZustand' },
  { key: 'ist-score', labelKey: 'workplace.steps.istScore' },
  { key: 'sov-weights', labelKey: 'workplace.steps.sovWeights' },
  { key: 'solution-builder', labelKey: 'workplace.steps.solutionBuilder' },
  { key: 'results', labelKey: 'workplace.steps.results' },
];

const stepIndex = (step: WorkplaceSovStep) => STEPS.findIndex((s) => s.key === step);

export const WorkplaceProgressBar: React.FC<WorkplaceProgressBarProps> = ({
  currentStep,
  onStepClick,
}) => {
  const { t } = useLanguage();
  const currentIdx = stepIndex(currentStep);

  return (
    <nav
      aria-label="Progress"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        padding: '12px 0',
        overflowX: 'auto',
      }}
    >
      {STEPS.map((step, idx) => {
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const isClickable = true; // Allow navigation to any step

        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div
                style={{
                  flex: '1 1 24px',
                  height: 2,
                  background: idx <= currentIdx ? theme.primary : theme.border,
                  minWidth: 16,
                }}
              />
            )}
            <button
              onClick={() => isClickable && onStepClick(step.key)}
              disabled={!isClickable}
              aria-current={isCurrent ? 'step' : undefined}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                background: 'none',
                border: 'none',
                cursor: isClickable ? 'pointer' : 'default',
                padding: '4px 6px',
                minWidth: 0,
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  color: isCurrent || isCompleted ? '#fff' : theme.muted,
                  background: isCurrent
                    ? theme.primary
                    : isCompleted
                      ? theme.secondary
                      : theme.cardBg,
                  border: `2px solid ${isCurrent ? theme.primary : isCompleted ? theme.secondary : theme.border}`,
                  transition: 'all 0.2s',
                }}
              >
                {isCompleted ? '✓' : idx + 1}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: isCurrent ? 700 : 400,
                  color: isCurrent ? theme.text : theme.muted,
                  whiteSpace: 'nowrap',
                }}
              >
                {t(step.labelKey as any)}
              </span>
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
};
